"use client";

import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Loader2, Mail, Users } from "lucide-react";
import { useSDK } from "@/@sdk/sdk.hooks";
import { selectAllActiveAndInactiveUsers } from "@/store/modules/users/users.selectors";
import { getUserName } from "./helpers/get-user-name";

type TeamMembersSummaryProps = {
  refreshKey?: number;
};

export function TeamMembersSummary({ refreshKey = 0 }: TeamMembersSummaryProps) {
  const users = useSelector(selectAllActiveAndInactiveUsers);
  const {
    data: activeInvites,
    isLoading: isLoadingInvites,
    reload: reloadInvites,
  } = useSDK((SDK) => SDK.getAllActiveInvitation(), [], false, []);

  useEffect(() => {
    void reloadInvites();
  }, [refreshKey, reloadInvites]);

  const teamMembers = useMemo(
    () =>
      users
        .filter(
          (user) =>
            !user.metaData.isDeleted && user.metaData.isActive === true
        )
        .map((user) => ({
          id: user.id!,
          name: getUserName(user),
          email: user.credentials.email,
        })),
    [users]
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">
            Team members ({teamMembers.length})
          </h3>
        </div>

        {teamMembers.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {teamMembers.map((member) => (
              <li
                key={member.id}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="font-medium">{member.name}</span>
                <span className="truncate text-muted-foreground">
                  {member.email}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            No team members yet.
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Pending invitations</h3>
        </div>

        {isLoadingInvites ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading invitations…
          </div>
        ) : activeInvites.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {activeInvites.map((invite) => (
              <li
                key={invite.email}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="font-medium">{invite.email}</span>
                <span className="text-muted-foreground">Invitation sent</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            No pending invitations.
          </p>
        )}
      </div>
    </div>
  );
}
